/**
 * Guest Email Verification Component
 * Handles email verification flow for guest collection access
 */

import React, { useState } from 'react';
import { sendGuestVerificationCode, confirmGuestVerificationCode } from '../services/collectionSharingService';
import '../styles/components/guestEmailVerification.css';

interface GuestEmailVerificationProps {
  shareToken: string;
  onVerified: (email: string) => void;
  onCancel?: () => void;
}

const GuestEmailVerification: React.FC<GuestEmailVerificationProps> = ({
  shareToken,
  onVerified,
  onCancel
}) => {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await sendGuestVerificationCode(email, shareToken);
      setCodeSent(true);
      setStep('code');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await confirmGuestVerificationCode(email, code, shareToken);
      // Store verified email in localStorage for this session
      localStorage.setItem(`guest_verified_${shareToken}`, email);
      onVerified(email);
    } catch (err: any) {
      setError(err.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError(null);
    setCode('');

    try {
      await sendGuestVerificationCode(email, shareToken);
      setCodeSent(true);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="guest-verification-container">
      <div className="guest-verification-card">
        <div className="verification-header">
          <h2>🔐 Email Verification Required</h2>
          <p>This collection requires email verification to access.</p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendCode} className="verification-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                autoFocus
                disabled={loading}
              />
              <p className="help-text">
                We'll send you a verification code to confirm your identity.
              </p>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !email}
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} className="verification-form">
            <div className="email-display">
              <span>Sending code to:</span>
              <strong>{email}</strong>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setCodeSent(false);
                  setError(null);
                }}
                className="btn-link"
                disabled={loading}
              >
                Change
              </button>
            </div>

            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
                disabled={loading}
                className="code-input"
              />
              <p className="help-text">
                Enter the 6-digit code sent to your email.
              </p>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            {codeSent && !error && (
              <div className="success-message">
                ✅ Verification code sent! Check your email.
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & Access'}
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                className="btn-secondary"
                disabled={loading}
              >
                Resend Code
              </button>
            </div>
          </form>
        )}

        <div className="verification-footer">
          <p>
            <strong>Why email verification?</strong><br />
            The collection owner requires email verification to track who accesses this collection.
            Your email will be visible to the owner.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestEmailVerification;
